import { motion } from 'framer-motion';
import { Brain, BarChart3, Shield, Heart, Zap, Target, CheckCircle, ArrowRight } from 'lucide-react';

const InsuranceSolution = () => {
  const benefits = [
    {
      icon: Heart,
      title: "Prevent costly claims with early risk detection",
      description: "AI-powered health monitoring identifies at-risk members before conditions become expensive",
      color: "#0077B6"
    },
    {
      icon: Zap,
      title: "Automate and accelerate approval processes",
      description: "Smart workflows reduce processing time by 70% while maintaining accuracy",
      color: "#03045E"
    },
    {
      icon: Shield,
      title: "Detect and stop fraud before it drains your budget",
      description: "Advanced pattern recognition catches suspicious claims and provider behavior",
      color: "#90E0EF"
    },
    {
      icon: Target,
      title: "Deliver value-added services that retain clients",
      description: "Personalized wellness programs and proactive care keep members engaged",
      color: "#00B4D8"
    }
  ];

  const features = [
    { 
      title: "Predictive Risk Analytics", 
      description: "Identify high-risk members before claims spike",
      metric: "35% cost reduction"
    },
    { 
      title: "Real-time Fraud Detection", 
      description: "Stop fraudulent claims before payment",
      metric: "98.5% accuracy"
    },
    { 
      title: "Automated Approvals", 
      description: "Streamline pre-authorization processes",
      metric: "70% faster processing"
    },
    { 
      title: "Member Engagement", 
      description: "Proactive wellness and prevention programs",
      metric: "45% retention increase"
    }
  ];

  return (
    <section id="solution" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 bg-[#90E0EF]/20 border border-[#90E0EF]/30 rounded-full mb-6">
            <Brain className="w-4 h-4 text-[#03045E] mr-2" />
            <span className="text-[#03045E] font-semibold text-sm">From Cost Center to Competitive Advantage</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-[#03045E] mb-6">
            <span className="text-[#0077B6]">AI-Powered</span>, Data-Driven, Customer-Centric
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            HIERSTO is the insurance partner platform that turns risk into opportunity — empowering you to reduce costs, 
            improve efficiency, and stand out in the market.
          </p>
        </motion.div>

        {/* Core Benefits */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-4">Core Benefits</h3>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative"
              >
                <div className="bg-white rounded-2xl rounded-b-none p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 h-full">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="p-3 rounded-xl  flex-shrink-0"
                      style={{ backgroundColor: `${benefit.color}20` }}
                    >
                      <benefit.icon 
                        className="w-6 h-6" 
                        style={{ color: benefit.color }}
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-[#03045E] mb-3 group-hover:text-[#0077B6] transition-colors">
                        {benefit.title}
                      </h4>
                      <p className="text-gray-600 leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Hover effect */}
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    transition={{ duration: 0.8, delay: 0.5 + index * 0.1 }}
                    viewport={{ once: true }}
                    className="absolute bottom-0 left-0 h-1 rounded-xl"
                    style={{ backgroundColor: benefit.color }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Interactive Features Demo */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          
          {/* Left: Features List */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h3 className="text-3xl font-bold text-[#03045E] mb-8">
              Platform Capabilities
            </h3>
            
            <div className="space-y-4">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group cursor-pointer"
                >
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#90E0EF]/50 transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <CheckCircle className="w-5 h-5 text-[#0077B6]" />
                          <h4 className="font-bold text-[#03045E] group-hover:text-[#0077B6] transition-colors">
                            {feature.title}
                          </h4>
                        </div>
                        <p className="text-gray-600 text-sm">
                          {feature.description}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-lg font-bold text-[#0077B6]">
                          {feature.metric}
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-[#0077B6] group-hover:translate-x-1 transition-all duration-300 ml-auto" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: Interactive Dashboard */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-white rounded-3xl shadow-2xl p-8 border border-[#90E0EF]/20">
              <div className="space-y-6">
                
                {/* Header */}
                <div className="flex items-center justify-between">
                  <h4 className="text-xl font-bold text-[#03045E]">HIERSTO Analytics Dashboard</h4>
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-[#90E0EF] rounded-full"></div>
                    <div className="w-3 h-3 bg-[#0077B6] rounded-full"></div>
                    <div className="w-3 h-3 bg-[#03045E] rounded-full"></div>
                  </div>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="bg-[#90E0EF]/10 p-4 rounded-xl"
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <BarChart3 className="w-4 h-4 text-[#0077B6]" />
                      <span className="text-sm text-gray-600">Claims Prevented</span>
                    </div>
                    <div className="text-2xl font-bold text-[#03045E]">1,247</div>
                    <div className="text-xs text-green-600">↑ 23% this month</div>
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="bg-[#0077B6]/10 p-4 rounded-xl"
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <Shield className="w-4 h-4 text-[#0077B6]" />
                      <span className="text-sm text-gray-600">Fraud Detected</span>
                    </div>
                    <div className="text-2xl font-bold text-[#03045E]">AED 2.1M</div>
                    <div className="text-xs text-green-600">↑ 15% this month</div>
                  </motion.div>
                </div>

                {/* Real-time Activity */}
                <div className="space-y-3">
                  <h5 className="text-sm font-semibold text-[#03045E]">Real-time Activity</h5>
                  {[
                    { action: "High-risk member identified", time: "2 min ago", type: "warning" },
                    { action: "Fraudulent claim blocked", time: "5 min ago", type: "success" },
                    { action: "Pre-approval automated", time: "8 min ago", type: "info" }
                  ].map((activity, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${
                          activity.type === 'warning' ? 'bg-orange-400' :
                          activity.type === 'success' ? 'bg-green-400' : 'bg-blue-400'
                        }`}></div>
                        <span className="text-sm text-gray-700">{activity.action}</span>
                      </div>
                      <span className="text-xs text-gray-500">{activity.time}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Action Button */}
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  viewport={{ once: true }}
                  onClick={() => {
                    const element = document.getElementById('partnership-journey');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full bg-[#03045E] text-white py-3 rounded-xl font-semibold hover:bg-[#03045E]/90 transition-colors"
                >
                  Explore Partnership Journey
                </motion.button>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
              viewport={{ once: true }}
              className="absolute -top-4 -right-4 bg-[#0077B6] text-white p-3 rounded-full shadow-lg"
            >
              <Brain className="w-5 h-5" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              viewport={{ once: true }}
              className="absolute -bottom-4 -left-4 bg-[#90E0EF] text-[#03045E] p-3 rounded-full shadow-lg"
            >
              <BarChart3 className="w-5 h-5" />
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-[#CAF0F8]/30 to-[#90E0EF]/30 rounded-3xl p-8 border border-[#90E0EF]/30">
            <h3 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-4">
              Ready to transform challenges into competitive advantages?
            </h3>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              See how our proven four-stage partnership journey delivers measurable ROI from day one.
            </p>
            <motion.button
              onClick={() => {
                const element = document.getElementById('partnership-journey');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-[#03045E] text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#03045E]/90 transition-all duration-300 shadow-lg inline-flex items-center space-x-2"
            >
              <span>Explore Partnership Journey</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InsuranceSolution;
